import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
    MatTable,
    MatTableModule,
    MatTableDataSource,
} from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { CompanyContactInfo } from '../../interfaces/CompanyContactInfo';
import { ContactMediumType } from '../../interfaces/ContactMediumType';
import { ContactMediumTypeResponse } from '../../interfaces/ContactMediumTypeResponse';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-edit-company',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './m-edit-company.component.html',
    styleUrl: './m-edit-company.component.scss',
})
export class MEditCompanyComponent implements OnInit {
    // isToggled
    isToggled = false;

    // Form
    editCompanyForm: FormGroup;
    editContactForm: FormGroup;

    identificationTypes: any = [
        {
            full: 'Registro civil',
            short: 'RC',
        },
        {
            full: 'Tarjeta de identidad',
            short: 'TI',
        },
        {
            full: 'Cédula de ciudadanía',
            short: 'CC',
        },
        {
            full: 'Tarjeta de extranjería',
            short: 'TE',
        },
        {
            full: 'Cédula de extranjería',
            short: 'CE',
        },
        {
            full: 'Número de identificación tributaria',
            short: 'NIT',
        },
        {
            full: 'Pasaporte',
            short: 'PP',
        },
        {
            full: 'Permiso especial de permanencia',
            short: 'PEP',
        },
        {
            full: 'Documento de identificación extranjero',
            short: 'DIE',
        },
    ];

    // Array of type of periods
    periodTypes: string[] = ['day', 'month', 'year'];

    selectedPeriod: string = '';

    // Array of contact medium types
    contactMediumTypes: ContactMediumType[] = [];

    selectedContactMediumType: ContactMediumType;

    displayedColumns: string[] = ['select', 'contactMediumName', 'contact'];
    contactInfos: CompanyContactInfo[] = [];
    dataSource = new MatTableDataSource<CompanyContactInfo>(this.contactInfos);
    selection = new SelectionModel<CompanyContactInfo>(true, []);

    // Show alert
    showAlert: boolean = false;

    // Id
    id: string;

    // Company
    editCompany: Company | undefined;

    readonly dialog = inject(MatDialog);

    @ViewChild(MatTable) table: MatTable<CompanyContactInfo>;

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.editCompanyForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(200)]],
            identification: [
                '',
                [Validators.required, Validators.maxLength(40)],
            ],
            address: ['', [Validators.required, Validators.maxLength(300)]],
            startContract: [{ value: '', disabled: true }, Validators.required],
            amount: ['', [Validators.min(1), Validators.max(1000)]],
            period: ['', []],
            endContract: [{ value: '', disabled: true }, Validators.required],
        });
        this.editContactForm = this.fb.group({
            contact: ['', [Validators.required, Validators.maxLength(200)]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.apiService
            .getContactMediumType()
            .subscribe((response: ContactMediumTypeResponse) => {
                this.contactMediumTypes.push(...response.contactMediumTypes);
            });
        this.route.params.subscribe((params) => {
            this.id = params['id'];

            this.apiService
                .getCompanyById(this.id)
                .subscribe((data: CompanyResponse) => {
                    this.editCompany = data.company;
                    if (this.editCompany.contactInfos) {
                        this.contactInfos.push(
                            ...this.editCompany.contactInfos
                        );
                        this.table.renderRows();
                    }
                });
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    /**
     * removeSelectedRows
     */
    removeSelectedRows() {
        this.selection.selected.forEach((item) => {
            let index: number = this.contactInfos.findIndex((d) => d === item);
            console.log(this.contactInfos.findIndex((d) => d === item));
            this.contactInfos.splice(index, 1);
            this.dataSource = new MatTableDataSource<CompanyContactInfo>(
                this.contactInfos
            );
        });
        this.selection = new SelectionModel<CompanyContactInfo>(true, []);
    }

    /**
     * addTime
     */
    private addTime(date: string, valueToAdd: number, period: string): Date {
        const startDate = new Date(date);
        const endDate = new Date(
            startDate.getFullYear() + (period == 'year' ? valueToAdd : 0),
            startDate.getMonth() + (period == 'month' ? valueToAdd : 0),
            startDate.getDate() + (period == 'day' ? valueToAdd : 0)
        );
        return endDate;
    }

    /**
     * changeEndContract
     */
    changeEndContract() {
        if (
            this.editCompanyForm.get('amount')?.value &&
            this.selectedPeriod &&
            this.editCompany
        ) {
            this.editCompanyForm.patchValue({
                endContract: this.addTime(
                    this.editCompany.startContract,
                    this.editCompanyForm.get('amount')?.value,
                    this.selectedPeriod
                ),
            });
        }
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.editCompanyForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        if (this.editCompany) {
            // Disable the form
            this.editCompanyForm.disable();

            // Hide the alert
            this.showAlert = false;

            // Edit Company
            this.editCompany.contactInfos = this.contactInfos;
            this.apiService.updateCompany(this.id, this.editCompany).subscribe({
                next: (value) => {
                    console.info(value);
                    // Re-enable the form
                    this.editCompanyForm.enable();

                    // Show the alert
                    this.showAlert = true;
                },
                error: (error) => {
                    let errMsg: string = error;
                    const dialogRef = this.dialog.open(AlertDialogComponent, {
                        data: {
                            title: 'ERROR',
                            message: errMsg,
                        },
                    });
                    // Re-enable the form
                    this.editCompanyForm.enable();

                    // Show the alert
                    this.showAlert = true;
                },
                complete: () => {
                    this.router.navigateByUrl('/masters/companies');
                },
            });
        }
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    addContactInfo() {
        const newCompanyContactInfo: CompanyContactInfo = {
            ...this.editContactForm.value,
            contactMediumType: this.selectedContactMediumType,
        };
        this.contactInfos.push(newCompanyContactInfo);
        this.table.renderRows();
        this.classApplied = !this.classApplied;
    }
}
